import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { ArrowRightIcon, CheckmarkIcon } from 'assets/icons';
import { Paragraph } from 'common/components';
import { openInNewTab } from 'common/helpers';
import { Button } from 'components/common';
import { Chip } from 'components/common/Chips/Chip';
import { Divider } from 'components/styled/Divider';
import { Flex } from 'components/styled/Flex';

import { Card, Description, FeatureItem, FeatureList, Title } from './PlansAndPricing.styled';

export const PlansAndPricing = () => {
  const { t } = useTranslation();
  const { colors, fontFamily, fontSize, spacing } = useTheme();

  const Check = () => (
    <Flex alignItems="center">
      <CheckmarkIcon width={24} height={24} stroke={colors.primary} />
    </Flex>
  );

  return (
    <Flex flexDirection="column" alignItems="center" width="100%">
      {/* Header */}
      <Flex flexDirection="column" alignItems="center" margin={`0 0 ${spacing.two} 0`}>
        <Flex margin={`0 0 ${spacing.two_1} 0`}>
          <Chip
            text={t('Plans & Pricing')}
            color="primary_dark"
            bgColor="primary_10"
            borderColor="primary_dark"
          />
        </Flex>
        <Paragraph
          fontFamily={fontFamily.medium}
          fontSize="1.75rem"
          color={colors.black_100}
          margin={`0 ${spacing.one} ${spacing.one_1} ${spacing.one}`}
          textAlign="center"
        >
          {t('Simple plans, clear prices')}
        </Paragraph>
        <Paragraph
          fontFamily={fontFamily.regular}
          fontSize={fontSize.lg}
          color={colors.black_80}
          margin="0 1rem"
          lineHeight="1.5rem"
          textAlign="center"
          whiteSpace="pre-line"
        >
          {t(
            'Choose the plan that best suits your needs and take advantage of everything we offer.',
          )}
        </Paragraph>
      </Flex>

      {/* Cards */}
      <Flex
        gap={spacing.one_3}
        justifyContent="center"
        flexWrap="wrap"
        width="100%"
        margin={`0 0 ${spacing.three}`}
      >
        {/* Basic Plan Card */}
        <Card>
          <Flex margin="0 0 1rem">
            <Chip text={t('Basic plan')} color="primary_dark" bgColor="primary_10" />
          </Flex>
          <Flex gap="0.25rem" margin="0 0 0.25rem 0">
            <Paragraph
              fontFamily={fontFamily.semiBold}
              fontSize="2.375rem"
              color={colors.black_100}
              margin="0"
            >
              12 EUR
            </Paragraph>
            <Paragraph
              fontFamily={fontFamily.regular}
              fontSize={fontSize.sm}
              color={colors.black_80}
              margin="0"
            >
              {t('monthly per rental')}
            </Paragraph>
          </Flex>
          <Paragraph
            fontFamily={fontFamily.medium}
            fontSize={fontSize.md}
            color={colors.black_80}
            margin={`0 0 ${spacing.two_1} 0`}
            lineHeight="1.5rem"
            whiteSpace="pre-line"
          >
            {t('For private landlords (1-20 units)')}
          </Paragraph>
          <Divider margin={`0 0 ${spacing.one_3}`} />
          <FeatureList>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Property Managment System')}</Title>
                <Description>{t('simple and intuitive management of your rentals')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Channel Manager Basic')}</Title>
                <Description>
                  {t('connect all units on Booking.com, Airbnb, Expedia, Vrbo and Adriagate')}
                </Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Unified inbox')}</Title>
                <Description>{t('all inquiries from OTA channels in one place')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Central calendar')}</Title>
                <Description>{t('view reservations in real time')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Basic automation and notifications')}</Title>
                <Description>{t('notifications and reminders without effort')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('eVisitor guest registration')}</Title>
                <Description>{t('fast and easy registration')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Basic analytics')}</Title>
                <Description>{t('insight into key data (coming soon)')}</Description>
              </Flex>
            </FeatureItem>
          </FeatureList>
          <Flex
            flexDirection="column"
            alignItems="center"
            margin={`${spacing.one_2} 0 0 0`}
            gap={spacing.zero_3}
            width="100%"
          >
            <Paragraph
              fontFamily={fontFamily.regular}
              fontSize={fontSize.xxs}
              color={colors.black_100}
              margin="0"
            >
              {t('15 days free trial.')}
            </Paragraph>
            <Button
              label="Contact us"
              color="blue"
              width="100%"
              onClick={() => openInNewTab('https://www.rentistic.com/en/contact/')}
              rightIcon={
                <Flex margin="0 0 0 0.5rem">
                  <ArrowRightIcon width={24} height={24} stroke={colors.white} />
                </Flex>
              }
            />
          </Flex>
        </Card>

        {/* Pro Plan Card */}
        <Card>
          <Flex margin="0 0 1rem">
            <Chip text={t('Pro plan')} color="green_90" bgColor="green_10" />
          </Flex>
          <Paragraph
            fontFamily={fontFamily.semiBold}
            fontSize="1.1875rem"
            color={colors.black_100}
            margin={`0 0 0.25rem 0`}
            lineHeight="2.5rem"
          >
            {t('Send price inquiry')}
          </Paragraph>
          <Paragraph
            fontFamily={fontFamily.medium}
            fontSize={fontSize.md}
            color={colors.black_80}
            margin={`0 0 ${spacing.two_1} 0`}
            lineHeight="1.5rem"
            whiteSpace="pre-line"
          >
            {t('For agencies and larger property managers (20+ units)')}
          </Paragraph>
          <Divider margin={`0 0 ${spacing.one_3}`} />
          <FeatureList>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Property Managment System')}</Title>
                <Description>
                  {t('advanced management of multiple units and capacities')}
                </Description>
              </Flex>
            </FeatureItem>

            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Channel Manager Pro')}</Title>
                <Description>{t('over 100 integrations with leading OTA channels')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Unified inbox with AI support')}</Title>
                <Description>
                  {t('faster and smarter communication for the 4 largest OTA channels')}
                </Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Central calendar')}</Title>
                <Description>{t('view and manage reservations in real time')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Automation and notification system')}</Title>
                <Description>{t('smart reminders for guests and landlords')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Online Check-In and eVisitor registration')}</Title>
                <Description>{t('completely digital experience for guests')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Rentistic LITE owner portal')}</Title>
                <Description>{t('provide landlords with a better experience')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Financial module')}</Title>
                <Description>{t('invoice creation and fiscalization (1.0 and 2.0)')}</Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Task Management')}</Title>
                <Description>
                  {t('organize your team, user access and operational tasks')}
                </Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Advanced search')}</Title>
                <Description>
                  {t('explore and create offers with more than 50 filters')}
                </Description>
              </Flex>
            </FeatureItem>
            <FeatureItem>
              <Check />
              <Flex flexDirection="column">
                <Title>{t('Advanced analytics')}</Title>
                <Description>{t('detailed insights and reports (coming soon)')}</Description>
              </Flex>
            </FeatureItem>
          </FeatureList>
          <Flex
            flexDirection="column"
            alignItems="center"
            margin={`${spacing.one_2} 0 0 0`}
            gap={spacing.zero_3}
            width="100%"
          >
            <Paragraph
              fontFamily={fontFamily.regular}
              fontSize={fontSize.xxs}
              color={colors.black_100}
              margin="0"
            >
              {t('15 days free trial.')}
            </Paragraph>
            <Button
              label="Contact us"
              color="blue"
              width="100%"
              onClick={() => openInNewTab('https://www.rentistic.com/en/contact/')}
              rightIcon={
                <Flex margin="0 0 0 0.5rem">
                  <ArrowRightIcon width={24} height={24} stroke={colors.white} />
                </Flex>
              }
            />
          </Flex>
        </Card>
      </Flex>
    </Flex>
  );
};
